import React, {
  KeyboardEvent,
  ReactElement,
  ReactNode,
  useId,
  useRef,
} from 'react'
import { useTranslate } from 'src/localization/Trns'

import * as style from './TransactionDetailTabs.style'

export type TransactionDetailTab = 'overview' | 'activities'

interface TransactionDetailTabsProps {
  activeTab: TransactionDetailTab
  activities: ReactNode
  header: ReactNode
  overview: ReactNode
  onTabChange: (tab: TransactionDetailTab) => void
}

const TABS: TransactionDetailTab[] = ['overview', 'activities']

const TransactionDetailTabs = ({
  activeTab,
  activities,
  header,
  overview,
  onTabChange,
}: TransactionDetailTabsProps): ReactElement => {
  const translate = useTranslate()
  const id = useId()
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])

  const selectTabFromKeyboard = (
    event: KeyboardEvent<HTMLButtonElement>,
    currentIndex: number,
  ): void => {
    let nextIndex: number | undefined

    switch (event.key) {
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + TABS.length) % TABS.length
        break
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % TABS.length
        break
      case 'Home':
        nextIndex = 0
        break
      case 'End':
        nextIndex = TABS.length - 1
        break
      default:
        return
    }

    event.preventDefault()
    const nextTab = TABS[nextIndex]
    onTabChange(nextTab)
    tabRefs.current[nextIndex]?.focus()
  }

  return (
    <div css={style.container}>
      <div css={style.header}>{header}</div>
      <div
        css={style.tabList}
        role="tablist"
        aria-label={translate('Transaction Details')}
      >
        {TABS.map((tab, index) => {
          const selected = tab === activeTab
          const label = tab === 'overview' ? 'Overview' : 'Activity'

          return (
            <button
              key={tab}
              ref={(element) => {
                tabRefs.current[index] = element
              }}
              css={style.tab}
              id={`${id}-${tab}-tab`}
              type="button"
              role="tab"
              aria-controls={`${id}-${tab}-panel`}
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => onTabChange(tab)}
              onKeyDown={(event) => selectTabFromKeyboard(event, index)}
            >
              {translate(label)}
            </button>
          )
        })}
      </div>
      <div
        css={[style.panel, style.overviewPanel]}
        id={`${id}-overview-panel`}
        role="tabpanel"
        aria-labelledby={`${id}-overview-tab`}
        hidden={activeTab !== 'overview'}
        tabIndex={0}
      >
        {overview}
      </div>
      <div
        css={style.panel}
        id={`${id}-activities-panel`}
        role="tabpanel"
        aria-labelledby={`${id}-activities-tab`}
        hidden={activeTab !== 'activities'}
        tabIndex={0}
      >
        {activities}
      </div>
    </div>
  )
}

export default TransactionDetailTabs
